import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ChevronLeftIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  clearStoredPostAuthRedirect,
  consumePostAuthRedirect,
  getAuthDestination,
  hasExplicitPostAuthIntent,
} from '../../utils/authRedirect';
import { getPostAuthDefaultRoute, hasCompletedOnboarding } from '../../utils/onboarding';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const LoginForm = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!hasExplicitPostAuthIntent(location)) {
      clearStoredPostAuthRedirect();
    }
  }, [location]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get('error');
    if (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      if (error === 'oauth_account_conflict') {
        errorMessage =
          'An account with this email already exists. Please use the original sign-in method.';
      } else if (
        error === 'token_exchange_failed' ||
        error === 'failed_to_fetch_user_info'
      ) {
        errorMessage = 'Google authentication failed. Please try again.';
      } else if (error === 'oauth_failed') {
        errorMessage = 'OAuth authentication failed. Please try again.';
      }
      toast.error(errorMessage);
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);

    const result = await login(formData);

    if (result.success) {
      const fallback = getPostAuthDefaultRoute(result.user);
      const from = hasExplicitPostAuthIntent(location)
        ? consumePostAuthRedirect(
            getAuthDestination(location, fallback),
            (target) =>
              !(hasCompletedOnboarding(result.user) && target.startsWith('/onboarding'))
          )
        : fallback;
      navigate(from, { replace: true });
    } else {
      const errorMessage = result.message || 'Login failed. Please try again.';

      if (errorMessage.toLowerCase().includes('invalid email or password')) {
        setErrors((prev) => ({
          ...prev,
          email: errorMessage,
          password: errorMessage,
        }));
      } else if (errorMessage.toLowerCase().includes('account is deactivated')) {
        setErrors((prev) => ({ ...prev, email: errorMessage }));
      } else if (errorMessage.toLowerCase().includes('email')) {
        setErrors((prev) => ({ ...prev, email: errorMessage }));
      } else if (errorMessage.toLowerCase().includes('password')) {
        setErrors((prev) => ({ ...prev, password: errorMessage }));
      } else {
        toast.error(errorMessage);
      }
    }

    setIsLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    const result = await signInWithGoogle();

    if (!result?.success) {
      toast.error(
        result?.message || 'Google authentication failed. Please try again.'
      );
      setIsGoogleLoading(false);
      return;
    }

    if (!result.redirecting) {
      const fallback = getPostAuthDefaultRoute(result.user);
      const from = hasExplicitPostAuthIntent(location)
        ? consumePostAuthRedirect(
            getAuthDestination(location, fallback),
            (target) =>
              !(hasCompletedOnboarding(result.user) && target.startsWith('/onboarding'))
          )
        : fallback;
      navigate(from, { replace: true });
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="mobile-auth-container relative overflow-hidden">
      <div
        className="lg:hidden absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('/home/photo1_home.jpg')`,
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-gray-900/20 to-black/20"></div>
      </div>

      <div
        className="lg:hidden absolute left-4 z-20"
        style={{ top: 'calc(var(--app-safe-area-top) + 1rem)' }}
      >
        <Link
          to="/"
          className="flex items-center justify-center w-10 h-10 bg-black/20 hover:bg-black/30 rounded-full backdrop-blur-sm transition-colors"
        >
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </Link>
      </div>

      <div
        className="lg:hidden absolute left-1/2 z-20 -translate-x-1/2 transform"
        style={{ top: 'calc(var(--app-safe-area-top) + 1.5rem)' }}
      >
        <img src="/logo_white.webp" alt="OKIO" className="h-8 w-auto mx-auto" />
      </div>

      <div className="relative z-10 h-full flex flex-col lg:flex-row">
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('/home/photo1_home.jpg')`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-gray-900/20 to-black/20"></div>
          </div>
        </div>

        <div className="flex-1 lg:w-1/2 flex items-end lg:items-center justify-center lg:bg-white lg:dark:bg-gray-900 lg:py-12 px-0 lg:px-8">
          <div className="w-full lg:max-w-md bg-white dark:bg-gray-900 lg:bg-transparent rounded-t-3xl lg:rounded-none shadow-2xl lg:shadow-none px-6 pt-6 pb-5 lg:p-0 space-y-4 lg:space-y-8">
            <div className={`mb-5 ${showEmailForm ? '' : 'text-center'}`}>
              <Link to="/" className="hidden lg:block">
                <img
                  src="/logo_black.webp"
                  alt="OKIO"
                  className="h-8 w-auto mx-auto mb-4 dark:hidden"
                />
                <img
                  src="/logo_white.webp"
                  alt="OKIO"
                  className="h-8 w-auto mx-auto mb-4 hidden dark:block"
                />
              </Link>
              {showEmailForm ? (
                <div className="mt-0 lg:mt-4 grid grid-cols-[auto_1fr_auto] items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShowEmailForm(false)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-800 dark:border-white/10 dark:text-gray-400 dark:hover:border-white/20 dark:hover:text-gray-200"
                    aria-label="Back"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <h2 className="text-center text-xl font-extrabold text-gray-900 dark:text-white">
                    Sign in with email
                  </h2>
                  <div aria-hidden="true" className="w-10"></div>
                </div>
              ) : (
                <h2 className="mt-0 lg:mt-4 text-xl font-extrabold text-gray-900 dark:text-white max-w-[220px] mx-auto">
                  Sign up or log in to start exploring
                </h2>
              )}
            </div>

            <form className="space-y-4 lg:space-y-6" onSubmit={handleSubmit}>
              {!showEmailForm ? (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    disabled={isGoogleLoading}
                    className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 dark:border-white/10 text-sm font-medium rounded-xl text-gray-700 dark:text-gray-200 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all duration-200"
                  >
                    <GoogleIcon />
                    {isGoogleLoading
                      ? 'Continuing with Google...'
                      : 'Continue with Google'}
                  </button>

                  <div className="relative py-1">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200 dark:border-white/10"></div>
                    </div>
                    <div className="relative flex justify-center">
                      <span className="bg-white px-3 text-[11px] font-medium uppercase tracking-[0.18em] text-gray-400 dark:bg-gray-900 dark:text-gray-500">
                        Or
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowEmailForm(true)}
                    className="w-full flex items-center justify-center py-3 px-4 rounded-xl border border-transparent bg-[#163b22] text-sm font-semibold text-white transition-all duration-200 hover:bg-[#1b4a2a] dark:bg-[#1d4d2c] dark:hover:bg-[#225b34]"
                  >
                    Continue with email
                  </button>
                </div>
              ) : null}

              {showEmailForm && (
                <>
                  <div className="space-y-3 lg:space-y-4">
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Email address
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                          errors.email
                            ? 'border-red-400'
                            : 'border-gray-300 dark:border-gray-600'
                          } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-[#181818]`}
                        placeholder="Enter your email"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-400">
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label
                        htmlFor="password"
                        className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                      >
                        Password
                      </label>
                      <div className="mt-1 relative">
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          autoComplete="current-password"
                          value={formData.password}
                          onChange={handleChange}
                          className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                            errors.password
                              ? 'border-red-400'
                              : 'border-gray-300 dark:border-gray-600'
                          } placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-300 focus:border-transparent focus:z-10 sm:text-sm transition-colors bg-white dark:bg-[#181818]`}
                          placeholder="Enter your password"
                        />
                        <button
                          type="button"
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                          onClick={() => setShowPassword(!showPassword)}
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <EyeSlashIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                          ) : (
                            <EyeIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:text-gray-400 dark:hover:text-gray-300" />
                          )}
                        </button>
                      </div>
                      {errors.password && (
                        <p className="mt-1 text-sm text-red-400">
                          {errors.password}
                        </p>
                      )}
                      <div className="text-right mt-2">
                        <Link
                          to="/forgot"
                          state={{ from: location.state?.from || undefined }}
                          className="text-sm text-gray-900 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white transition-colors"
                        >
                          Forgot password?
                        </Link>
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-xl text-white bg-[#163b22] hover:bg-[#1b4a2a] dark:bg-[#1d4d2c] dark:hover:bg-[#225b34] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 dark:focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Signing in...
                      </div>
                    ) : (
                      'Sign in with email'
                    )}
                  </button>

                  <div className="space-y-2">
                    <p className="text-center text-sm text-gray-600 dark:text-gray-400">
                      Don&apos;t have an account yet?
                    </p>
                    <Link
                      to="/register"
                      state={{ from: location.state?.from || undefined }}
                      className="block w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-center text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"
                    >
                      Create account with email
                    </Link>
                  </div>
                </>
              )}

              <p className="px-2 pt-8 text-center text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                By continuing, you acknowledge our{' '}
                <Link
                  to="/privacy-policy"
                  className="underline underline-offset-2 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;